import {partition} from 'lodash';
import React from 'react';
import {Box} from '@mui/material';
import {Headline} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type CreatorGroupLabel from '@/types/creatorGroupLabel';
import useAgencyQuery from '@/hooks/queries/use-agency-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './ListDrawer.module.scss';
import {
  AllCreatorsButton,
  List,
  ListDrawerAccordion,
  NewListButton,
} from './components';
import useListProps from './hooks/useListProps';

const TRANSLATION_PREFIX = 'components.list-drawer';

export type ListDrawerProps = {
  testID?: string;
  labels: CreatorGroupLabel[];
  totalEntries: number;
};

function ListDrawer(props: ListDrawerProps) {
  const {testID = 'list-drawer', labels, totalEntries} = props;
  const brandId = useBrandId();
  const {currentBrand, data} = useAgencyQuery({brandId});
  const listProps = useListProps();

  const isSharedList = ({organizationLevel}: CreatorGroupLabel) =>
    organizationLevel === true;

  const [sharedLists, brandLists] = partition(labels, isSharedList);

  const brandListsLabel = translate(`${TRANSLATION_PREFIX}.brand-lists`, {
    brandName:
      currentBrand?.name ??
      translate(`${TRANSLATION_PREFIX}.brand-lists-brand-name-fallback`),
  });

  return (
    <div className={styles.container} data-testid={testID}>
      <Box className={styles.stickyHeader}>
        <NewListButton />
        <Headline size="xs" className={styles.sectionHeading}>
          {translate(`${TRANSLATION_PREFIX}.your-lists`)}
        </Headline>
        <AllCreatorsButton numberOfCreators={totalEntries} />
      </Box>
      {sharedLists.length > 0 ? (
        <>
          <ListDrawerAccordion
            label={
              data?.name
                ? translate('shared lists', {
                    agency: data.name,
                  })
                : translate('shared-lists-fallback')
            }
          >
            {sharedLists?.map((group) => (
              <List key={group.id} {...listProps(group)} />
            ))}
          </ListDrawerAccordion>
          <div className={styles.divider} />
        </>
      ) : null}
      <ListDrawerAccordion label={brandListsLabel}>
        {brandLists?.map((group) => (
          <List key={group.id} {...listProps(group)} />
        ))}
      </ListDrawerAccordion>
    </div>
  );
}

const ListDrawerMemo = React.memo(ListDrawer);

export default ListDrawerMemo;
